import { ActionIcon, Badge } from '@mantine/core';
import { X } from 'react-feather';
import { AttendanceRate } from '../../../../components/common';
import styles from './SessionDetailModal.module.css';
import { getZoneCode } from '../../../../utils/formatters';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatHeaderDate(dateStr: string | null, tz: string | null): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const timeZone = tz || 'America/New_York';
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });
    return `${formatter.format(date)} ${getZoneCode(timeZone, date)}`;
  } catch {
    return dateStr;
  }
}

export interface SessionDetailHeaderProps {
  sessionNumber: number | null;
  topicName: string | null;
  groupName: string;
  scheduledStart: string | null;
  timezone: string | null;
  displayStatus: { label: string; color: string };
  stats: {
    sessions: string;
    participants?: string;
    engagement: string;
    // MED-1266: canonical server attendance rate + marking coverage, rendered via AttendanceRate.
    attendanceCoverage: { rate: number | null; marked: number; population: number };
  } | null;
  coachName: string | null;
  onClose: () => void;
  /** Hide the coach tile (used when the viewer IS the coach — MED-732). */
  hideCoach?: boolean;
  /** Hide the header's inline close button (e.g. when the parent Drawer supplies its own). */
  hideCloseButton?: boolean;
}

export function SessionDetailHeader({
  sessionNumber,
  topicName,
  groupName,
  scheduledStart,
  timezone,
  displayStatus,
  stats,
  coachName,
  onClose,
  hideCoach = false,
  hideCloseButton = false,
}: SessionDetailHeaderProps) {
  const title = [
    sessionNumber ? `Session ${sessionNumber}` : null,
    topicName,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className={styles.header}>
      <div className={styles.sessionInfo}>
        <div className={styles.sessionTitle}>{title || groupName}</div>
        {scheduledStart && (
          <div className={styles.sessionDate}>
            {formatHeaderDate(scheduledStart, timezone)}
          </div>
        )}
        <Badge
          size="sm"
          color={displayStatus.color}
          variant="light"
          style={{ textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10 }}
        >
          {displayStatus.label}
        </Badge>
      </div>

      <div className={styles.statsGroup}>
        <div className={styles.statBox}>
          <div className={styles.statValue}>{stats?.sessions ?? '—'}</div>
          <div className={styles.statLabel}>Sessions</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>{stats?.participants ?? '—'}</div>
          <div className={styles.statLabel}>Participants</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>
            {stats ? (
              <AttendanceRate
                rate={stats.attendanceCoverage.rate}
                marked={stats.attendanceCoverage.marked}
                population={stats.attendanceCoverage.population}
                variant="stacked"
                size="16px"
                fw={600}
              />
            ) : (
              '—'
            )}
          </div>
          <div className={styles.statLabel}>Attendance</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>{stats?.engagement ?? '—'}</div>
          <div className={styles.statLabel}>Avg Engagement</div>
        </div>
      </div>

      {!hideCoach && (
        <div className={styles.coachBox}>
          {coachName ? (
            <>
              <div className={styles.coachAvatar}>{getInitials(coachName)}</div>
              <div className={styles.coachInfo}>
                <span className={styles.coachLabel}>Coach</span>
                <span className={styles.coachName}>{coachName}</span>
              </div>
            </>
          ) : (
            <div className={styles.coachInfo}>
              <span className={styles.coachLabel}>Coach</span>
              <span className={styles.coachName} style={{ color: 'var(--mantine-color-dimmed)' }}>
                No coach assigned
              </span>
            </div>
          )}
        </div>
      )}

      {!hideCloseButton && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          <X size={18} />
        </ActionIcon>
      )}
    </div>
  );
}

export default SessionDetailHeader;
